import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTramaClient } from '../context.js';
import type { TramaCart } from 'trama-types';

const CART_KEY = ['cart'];

export function useCart() {
  const client = useTramaClient();
  const queryClient = useQueryClient();
  const [cartId, setCartId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem('wb_cart_id');
  });

  const { data: cart, isLoading, error } = useQuery({
    queryKey: [...CART_KEY, cartId],
    queryFn: () =>
      cartId
        ? client.request<TramaCart | null>('/api/v1/cart', { params: { cartId } })
        : null,
    enabled: Boolean(cartId),
  });

  const persistCartId = useCallback((id: string) => {
    setCartId(id);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('wb_cart_id', id);
    }
  }, []);

  const addItem = useMutation({
    mutationFn: async (params: { productId: string; variantId?: string; quantity?: number }) => {
      const result = await client.request<TramaCart>('/api/v1/cart/items', {
        method: 'POST',
        body: { productId: params.productId, variantId: params.variantId, quantity: params.quantity ?? 1 },
      });
      if (!cartId) persistCartId(result.platformCartId);
      return result;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([...CART_KEY, cartId ?? data.platformCartId], data);
    },
  });

  const removeItem = useMutation({
    mutationFn: async (lineItemId: string) => {
      if (!cartId) throw new Error('No active cart');
      return client.request<TramaCart>(`/api/v1/cart/${cartId}/items/${lineItemId}`, { method: 'DELETE' });
    },
    onSuccess: (data) => {
      queryClient.setQueryData([...CART_KEY, cartId], data);
    },
  });

  const updateQuantity = useMutation({
    mutationFn: async (params: { lineItemId: string; quantity: number }) => {
      if (!cartId) throw new Error('No active cart');
      return client.request<TramaCart>(`/api/v1/cart/${cartId}/items/${params.lineItemId}`, {
        method: 'PATCH',
        body: { quantity: params.quantity },
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData([...CART_KEY, cartId], data);
    },
  });

  const goToCheckout = useCallback(async () => {
    if (!cartId) return;
    try {
      const result = await client.request<{ checkoutUrl: string }>('/api/v1/checkout', {
        method: 'POST',
        body: { cartId },
      });
      window.location.href = result.checkoutUrl;
    } catch (err) {
      console.error('Checkout failed', err);
    }
  }, [cartId, client]);

  const clearCart = useCallback(() => {
    setCartId(null);
    if (typeof window !== 'undefined') window.sessionStorage.removeItem('wb_cart_id');
    queryClient.removeQueries({ queryKey: CART_KEY });
  }, [queryClient]);

  return {
    cart: cart ?? null,
    isLoading,
    error: error as Error | null,
    addItem: (productId: string, variantId?: string, quantity?: number) =>
      addItem.mutateAsync({ productId, ...(variantId != null ? { variantId } : {}), ...(quantity != null ? { quantity } : {}) }),
    removeItem: (lineItemId: string) => removeItem.mutateAsync(lineItemId),
    updateQuantity: (lineItemId: string, quantity: number) =>
      updateQuantity.mutateAsync({ lineItemId, quantity }),
    goToCheckout,
    clearCart,
    cartId,
  };
}
